import { useTranslate } from "@refinedev/core";
import { Flex } from "antd";
import { CheckIcon } from "@phosphor-icons/react";
import styled from "styled-components";
import { useTeacherOnboarding } from "./teacherOnboardingContext";
import { I18N_PREFIX } from "./constants";
import type { TeacherStep } from "./types";
import { radius } from "@libertify/ui";

// The steps collapse into four named phases, mirroring the prototype's top
// stepper: Voice → Teaching → Protect → Your DNA.
const PHASES: { key: string; steps: TeacherStep[] }[] = [
  { key: "voice", steps: ["voice1", "voice2"] },
  { key: "teaching", steps: ["illustration", "structure", "depth", "autonomy"] },
  { key: "protect", steps: ["guardrail"] },
  { key: "dna", steps: ["finish"] },
];

const PhaseStepper = () => {
  const t = useTranslate();
  const { currentStep } = useTeacherOnboarding();
  const current = PHASES.findIndex((p) => p.steps.includes(currentStep));

  return (
    <Flex
      align="start"
      style={{ width: "100%", maxWidth: 560, padding: "0 4px" }}
    >
      {PHASES.map((phase, i) => {
        let state: StepState = "todo";
        if (i < current) state = "done";
        else if (i === current) state = "now";
        const isLast = i === PHASES.length - 1;
        return (
          <Flex
            key={phase.key}
            vertical
            align="center"
            flex={1}
            style={{ position: "relative", minWidth: 0 }}
          >
            {!isLast && <Bar $done={i < current} />}
            <Bubble $state={state}>
              {state === "done" ? <CheckIcon weight="bold" size={15} /> : i + 1}
            </Bubble>
            <Label $state={state}>{t(`${I18N_PREFIX}.phases.${phase.key}`)}</Label>
          </Flex>
        );
      })}
    </Flex>
  );
};

export default PhaseStepper;

// ─── Styled components ───

type StepState = "done" | "now" | "todo";
// Brand navy for the current-phase bubble — no antd token equivalent.
const NAVY = "#161a26";

const Bar = styled.div<{ $done: boolean }>`
  position: absolute;
  top: 14px;
  left: 50%;
  width: 100%;
  height: 2px;
  z-index: 1;
  background: ${({ theme, $done }) =>
    $done ? theme.colorPrimary : theme.colorBorder};
`;

const Bubble = styled.div<{ $state: StepState }>`
  z-index: 2;
  width: 30px;
  height: 30px;
  border-radius: ${radius.circle};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 700;
  transition: 0.25s;
  color: ${({ theme, $state }) =>
    $state === "todo" ? theme.colorTextSecondary : "#fff"};
  background: ${({ theme, $state }) => {
    if ($state === "done") return theme.colorPrimary;
    if ($state === "now") return NAVY;
    return theme.colorBgContainer;
  }};
  border: 2px solid
    ${({ theme, $state }) => {
      if ($state === "done") return theme.colorPrimary;
      if ($state === "now") return NAVY;
      return theme.colorBorder;
    }};
  box-shadow: ${({ $state }) =>
    $state === "now" ? "0 0 0 4px rgba(58, 212, 202, 0.3)" : "none"};
`;

const Label = styled.div<{ $state: StepState }>`
  margin-top: 7px;
  font-size: 11px;
  line-height: 1.25;
  text-align: center;
  max-width: 84px;
  color: ${({ theme, $state }) =>
    $state === "todo" ? theme.colorTextSecondary : theme.colorText};
  font-weight: ${({ $state }) => ($state === "now" ? 700 : 500)};
`;
